// Driver app shows PICKUP windows only (seller-provided times).
// Backend scheduling still keys on DeliveryWindow.id, so we pair each pickup
// window with its corresponding delivery window for the accept/select call.
// pickupWindows[i] ↔ deliveryWindows[i] is guaranteed by predispatch.service:
// both arrays are built from the same selectedIndices over the same
// sellerWindows, deliveryWindows just shifted by (disassembly + driveTime).
// Sorting each by start preserves the pairing because the shift is constant.

export type PairedWindow = {
  pickupStart: Date;
  pickupEnd: Date;
  deliveryWindowId: string;
  deliveryStartTime: string;
  deliveryEndTime: string;
  isSelected: boolean;
};

type PickupWin = { start: string; end: string };
type DeliveryWin = { id: string; startTime: string; endTime: string };

export function pairPickupWithDelivery(
  pickupWindows: PickupWin[] | undefined | null,
  deliveryWindows: DeliveryWin[] | undefined | null,
  scheduledWindowStart?: string | Date | null,
): PairedWindow[] {
  const pw = (pickupWindows || []).filter((w): w is PickupWin => !!(w && w.start && w.end));
  const dw = (deliveryWindows || []).filter((w): w is DeliveryWin => !!(w && w.id && w.startTime && w.endTime));
  if (pw.length === 0 || dw.length === 0) return [];

  const pwSorted = [...pw].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const dwSorted = [...dw].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const schedMs = scheduledWindowStart ? new Date(scheduledWindowStart).getTime() : null;

  // On a count mismatch, pair only as many as both arrays support (1:1 by index).
  const pairCount = Math.min(pwSorted.length, dwSorted.length);

  return pwSorted.slice(0, pairCount).map((p, i) => ({
    pickupStart: new Date(p.start),
    pickupEnd: new Date(p.end),
    deliveryWindowId: dwSorted[i].id,
    deliveryStartTime: dwSorted[i].startTime,
    deliveryEndTime: dwSorted[i].endTime,
    isSelected: schedMs != null && Math.abs(new Date(dwSorted[i].startTime).getTime() - schedMs) < 60_000,
  }));
}

export function hasPickupWindows(job: any): boolean {
  return Array.isArray(job?.pickupWindows) && job.pickupWindows.length > 0;
}
